import React from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedRef, useScrollOffset } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { Main } from '@expo/html-elements';
import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';
import { CustomNavigationHeader, useNavbarContainerHeight } from '@garnish/navigation';

import { SweetpassCheckoutTitle } from '../Title';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckoutContainer = (props: Props) => {
  const { children, title, footer } = props;

  const { minWidth, currentBreakpoint, match } = useResponsive();
  const hasHeader = currentBreakpoint.isXS && Boolean(title);

  // ─── Scroll Offset ───────────────────────────────────────────────────

  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffsetSV = useScrollOffset(scrollViewRef);

  // ─── Styles ──────────────────────────────────────────────────────────

  const { navbarPaddingXS, navbarPaddingSM } = useNavbarPadding();

  const contentContainerStyle = match([
    [styles.scrollContent, styles.stacked, navbarPaddingXS],
    [styles.scrollContent, styles.stacked, navbarPaddingSM],
    styles.scrollContent,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SafeAreaView edges={SAFE_AREA_EDGES} style={styles.safeAreaView}>
      <Animated.ScrollView
        ref={scrollViewRef}
        automaticallyAdjustKeyboardInsets
        showsVerticalScrollIndicator
        contentContainerStyle={contentContainerStyle}
        stickyHeaderIndices={STICKY_HEADER_INDICES}
      >
        <View>
          {hasHeader ? (
            <CustomNavigationHeader.Container
              safeAreaEdges={null}
              palette="oatmeal"
              buttonsReservedSpace="none"
              scrollOffsetSV={scrollOffsetSV}
              style={styles.header}
              contentStyle={styles.headerContent}
            >
              <SweetpassCheckoutTitle>{title}</SweetpassCheckoutTitle>
            </CustomNavigationHeader.Container>
          ) : null}
        </View>

        <Main style={minWidth.isMD ? styles.mainMD : [styles.stacked, styles.mainStacked]}>
          {children}
        </Main>

        {minWidth.isMD ? footer : null}
      </Animated.ScrollView>
    </SafeAreaView>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useNavbarPadding = () => {
  const { height: navbarHeight } = useNavbarContainerHeight();

  const navbarPaddingXS = useStyle(
    () => ({ paddingBottom: navbarHeight + theme.spacing['6'] }),
    [navbarHeight],
  );

  const navbarPaddingSM = useStyle(
    () => ({
      paddingTop: navbarHeight + theme.spacing['6'],
      paddingBottom: theme.spacing['6'],
    }),
    [navbarHeight],
  );

  return { navbarPaddingXS, navbarPaddingSM };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES = ['top', 'bottom'] as const;
const STICKY_HEADER_INDICES = [0];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  safeAreaView: {
    flex: 1,
    backgroundColor: theme.colors.OATMEAL,
  },
  scrollContent: {
    flexGrow: 1,
  },
  stacked: {
    gap: theme.spacing['6'],
  },
  header: {
    padding: theme.spacing['4'],
  },
  headerContent: {
    minHeight: 0,
  },
  mainStacked: {
    paddingHorizontal: theme.spacing['4'],
  },
  mainMD: {
    flex: 1,
    flexDirection: 'row',
  },
});

// ─── Props ───────────────────────────────────────────────────────────────────

type Props = {
  children: React.ReactNode;
  /**
   * Rendered in a sticky header on `XS`.
   */
  title?: string;
  footer?: React.ReactNode;
};
